import React, { useEffect, useState, useRef } from "react";
import { GoogleMap, Marker, Polygon } from "@react-google-maps/api";
import {
  Button,
  Box,
  Typography,
  FormControl,
  MenuItem,
  TextField,
  Alert,
  Snackbar,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { AppState } from "redux/store";
import {
  createPolygon,
  fetchAreasByCityId,
  fetchCities,
  getNetworkPartnerByAreaCode,
  getPolygonDetail,
  updatePolygon,
} from "redux/actions";
import { CityModel } from "types/models/CityModel";
import { AreaModel } from "types/models/AreaModel";
import { AxiosResponse } from "axios";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteMessage from "../PolygonTable/DeleteMessage";
interface MapLocation {
  lat: number;
  lng: number;
}

type PolygonEntry = {
  id: number;
  polygon: google.maps.Polygon;
  path: MapLocation[];
  deleteMarker: google.maps.Marker;
};


const PolygonUpdateComponent: React.FC = () => {
  const [deleteMarkerToRemove, setDeleteMarkerToRemove] = useState<google.maps.Marker | null>(null);

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [polygonToDelete, setPolygonToDelete] = useState<google.maps.Polygon | null>(null);
  const [networkPartners, setNetworkPartners] = useState<any[]>([]);
  const { isLoaded } = useOutletContext<{ isLoaded: boolean }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const polygonId = id ? Number(id) : undefined;
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY ?? "YOUR_BACKUP_API_KEY";
  const [networkPartner, setNetworkPartner] = useState<string>("");
  const defaultCenter: MapLocation = { lat: 28.6139, lng: 77.209 };
  const [polygonPaths, setPolygonPaths] = useState<MapLocation[][]>([]);
  const [location, setLocation] = useState<MapLocation | null>(null);
  const [polygonRefs, setPolygonRefs] = useState<google.maps.Polygon[]>([]);
  const [polygonDeleteMarkers, setPolygonDeleteMarkers] = useState<google.maps.Marker[]>([]);
  const [savedPolygon, setSavedPolygon] = useState<any>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [cities, setCities] = useState<CityModel[]>([]);
  const [selectedCity, setSelectedCity] = useState<string>("");
  const [areas, setAreas] = useState<AreaModel[]>([]);
  const [selectedArea, setSelectedArea] = useState<string>("");
  const [latitude, setLatitude] = useState<string | null>(null);
  const [longitude, setLongitude] = useState<string | null>(null);
  const [pinCode, setPinCode] = useState<string>("");
  const mapRef = useRef<google.maps.Map | null>(null);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawingVertices, setDrawingVertices] = useState<MapLocation[]>([]);
  const isDrawingModeRef = useRef(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [successSnackbar, setSuccessSnackbar] = useState(false);
  const [preLatLong, setPreLatLong] = useState(false);
  const { currentPolygon } = useSelector<AppState, AppState["master"]>(
    ({ master }) => master
  );
  const selectedAreaData = areas.find((area) => area.area === selectedArea);
  const [polygonEntries, setPolygonEntries] = useState<PolygonEntry[]>([]);
  const initializedRef = useRef(false);
  const [editablePolygonPath, setEditablePolygonPath] = useState<MapLocation[][]>([]);
  const [newlyCreatedPolygonPath, setNewlyCreatedPolygonPath] = useState<MapLocation[][]>([]);
  const [shouldInitialize, setShouldInitialize] = useState(false);
  const [originalEditablePolygonPaths, setOriginalEditablePolygonPaths] = useState<MapLocation[][]>([]);
  useEffect(() => {
    dispatch({ type: "RESET_POLYGON" });
    if (polygonId) {
      dispatch(getPolygonDetail(polygonId));
    }
  }, [dispatch, polygonId]);
  useEffect(() => {
    const fetchCitiesData = async () => {
      try {
        const response = (await dispatch(fetchCities())) as unknown as AxiosResponse;
        if (response?.status === 200) {
          setCities(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch cities:', error);
      }
    };
    fetchCitiesData();
  }, [dispatch]);


  useEffect(() => {
    if (

      !mapRef.current ||
      !currentPolygon ||
      currentPolygon.length === 0
    ) return;
    if (!mapRef.current || !currentPolygon || currentPolygon.length === 0) return;
    const map = mapRef.current;
    const polygon = currentPolygon[0];
    if (!polygon || !polygon.area_id || !polygon.city_name) return;
    if (initializedRef.current) return;

    initializedRef.current = true;
    console.log("Polygon Object:", polygon);
    if (!polygon.latlongarray) {
      setPreLatLong(true)
    }
    setSavedPolygon(polygon);
    setLatitude(polygon.latitude);
    setLongitude(polygon.longitude);
    setPinCode(polygon.pincode);
    setSelectedCity(polygon.city_name);

    const selectedCityData = cities.find((city) => city.name === polygon.city_name);
    if (!selectedCityData) return;

    dispatch<any>(fetchAreasByCityId(selectedCityData.id)).then(async (areas: AreaModel[]) => {
      setAreas(areas);

      const matchedArea = areas.find(area => area.areaId === polygon.area_id);
      if (!matchedArea) return;

      setSelectedArea(matchedArea.area);

      const address = `${matchedArea.area}, ${polygon.city_name}`;
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address }, (results, status) => {
        if (status === "OK" && results && results[0]) {
          const bounds = results[0].geometry.viewport || results[0].geometry.bounds;
          const map = mapRef.current;
          if (bounds && map) {
            map.fitBounds(bounds);
          } else if (map) {
            const location = results[0].geometry.location;
            map.setCenter(location);
            map.setZoom(15);
          }
        } else {
          console.error("Geocoding failed:", status);
        }
      });

      Promise.resolve(dispatch<any>(getNetworkPartnerByAreaCode(matchedArea.areaCode)))
        .then((response: any) => {
          setNetworkPartners(response?.status === 200 ? response.data.data : []);
        });

      if (Array.isArray(currentPolygon)) {
        const globalBounds = new window.google.maps.LatLngBounds();

        currentPolygon.forEach((polygon) => {
          if (!polygon?.latlongarray) return;

          const parsedArray = typeof polygon.latlongarray === 'string'
            ? JSON.parse(polygon.latlongarray)
            : polygon.latlongarray;

          const polygonGroups = Array.isArray(parsedArray[0]) ? parsedArray : [parsedArray];

          polygonGroups.forEach((path: MapLocation[]) => {
            const editable = Number(polygon.is_editable) === 1;
            const fillColor = editable ? '#ff0033' : '#6d6dae';

            const googleMapPath = path.map(coord => new window.google.maps.LatLng(coord.lat, coord.lng));

            const gPolygon = new window.google.maps.Polygon({
              paths: googleMapPath,
              map,
              editable,
              draggable: false,
              fillColor,
              fillOpacity: 0.3,
              strokeColor: fillColor,
              strokeOpacity: 0.8,
              strokeWeight: 2,
            });

            gPolygon.setMap(mapRef.current!);
            setPolygonRefs(prev => [...prev, gPolygon]);
            setPolygonPaths(prev => [...prev, path]);
            googleMapPath.forEach((latLng) => globalBounds.extend(latLng));

            if (editable && path.length > 0) {
              const index = editablePolygonPath.length;
              setEditablePolygonPath(prev => [...prev, path]);
              setOriginalEditablePolygonPaths(prev => [...prev, path]);
              addDeleteMarker(gPolygon);

              const pathRef = gPolygon.getPath();

              const updateEditablePath = () => {
                const updated = pathRef.getArray().map((point) => ({
                  lat: point.lat(),
                  lng: point.lng(),
                }));

                setEditablePolygonPath(prev => {
                  const newPaths = [...prev];
                  newPaths[index] = updated;
                  return newPaths;
                });

                const { lat, lng } = calculateCentroid(updated);
                setLatitude(lat.toString());
                setLongitude(lng.toString());

                fetch(
                  `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleMapsApiKey}`
                )
                  .then(res => res.json())
                  .then(data => {
                    const postalCode = data.results[0]?.address_components?.find(
                      (comp: any) => comp.types.includes("postal_code")
                    )?.long_name;
                    if (postalCode) setPinCode(postalCode);
                  })
                  .catch(err => console.error("Geocoding failed:", err));
              };

              pathRef.addListener("set_at", updateEditablePath);
              pathRef.addListener("insert_at", updateEditablePath);
              pathRef.addListener("remove_at", updateEditablePath);
            }
          });
        });


      }
      try {
        const response = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${googleMapsApiKey}`
        );
        const data = await response.json();

        if (data.results.length > 0) {
          const { lat, lng } = data.results[0].geometry.location;
          setLocation({ lat, lng });
          setLatitude(lat.toString());
          setLongitude(lng.toString());

          map.panTo({ lat, lng });
          map.setZoom(15);
        } else {
          map.panTo(defaultCenter);
          map.setZoom(10);
        }
      } catch (error) {
        console.error('Error fetching location from geocode API:', error);
        map.panTo(defaultCenter);
        map.setZoom(10);
      }
    });
  }, [mapRef.current, currentPolygon, dispatch, googleMapsApiKey, cities]);



  const handleConfirmDelete = () => {
    if (!polygonToDelete || !deleteMarkerToRemove) return;

    polygonToDelete.setMap(null);
    deleteMarkerToRemove.setMap(null);
    const deletedPath = polygonToDelete.getPath().getArray().map((point) => ({
      lat: point.lat(),
      lng: point.lng(),
    }));
    setPolygonRefs((prevPolygons) => {
      const index = prevPolygons.indexOf(polygonToDelete);
      if (index === -1) return prevPolygons;

      setPolygonPaths((prevPaths) => {
        const newPaths = [...prevPaths];
        newPaths.splice(index, 1);
        return newPaths;
      });

      return prevPolygons.filter((p) => p !== polygonToDelete);
    });
    setEditablePolygonPath((prev) => {
      if (!prev) return prev;
      const filtered = prev.filter(path => JSON.stringify(path) !== JSON.stringify(deletedPath));
      return filtered;
    });

    setNewlyCreatedPolygonPath((prev) => {
      if (!prev) return prev;
      const filtered = prev.filter(path => JSON.stringify(path) !== JSON.stringify(deletedPath));
      return filtered;
    });
    setPolygonDeleteMarkers((prevMarkers) =>
      prevMarkers.filter((m) => m !== deleteMarkerToRemove)
    );

    setOpenDeleteDialog(false);
    setPolygonToDelete(null);
    setDeleteMarkerToRemove(null);
  };





  const calculateCentroid = (path: MapLocation[]) => {
    const avg = path.reduce(
      (sum, p) => ({ lat: sum.lat + p.lat, lng: sum.lng + p.lng }),
      { lat: 0, lng: 0 }
    );
    return {
      lat: avg.lat / path.length,
      lng: avg.lng / path.length,
    };
  };


  const addDeleteMarker = (polygon: google.maps.Polygon) => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const path = polygon.getPath();

    const computeCentroid = () => {
      let latSum = 0;
      let lngSum = 0;
      const numPoints = path.getLength();
      for (let i = 0; i < numPoints; i++) {
        const latLng = path.getAt(i);
        latSum += latLng.lat();
        lngSum += latLng.lng();
      }
      return new window.google.maps.LatLng(latSum / numPoints, lngSum / numPoints);
    };

    const deleteMarker = new window.google.maps.Marker({
      position: computeCentroid(),
      map,
      icon: {
        url: "https://img.icons8.com/ios-filled/50/FF0000/trash.png",
        scaledSize: new window.google.maps.Size(15, 15),
      },
      title: "Delete Polygon",
      clickable: true,
    });

    deleteMarker.addListener("click", () => {
      setPolygonToDelete(polygon);
      setDeleteMarkerToRemove(deleteMarker);
      setOpenDeleteDialog(true);
    });

    const updateMarkerPosition = () => {
      deleteMarker.setPosition(computeCentroid());
    };

    path.addListener("insert_at", updateMarkerPosition);
    path.addListener("set_at", updateMarkerPosition);
    path.addListener("remove_at", updateMarkerPosition);

    setPolygonDeleteMarkers((prevMarkers) => [...prevMarkers, deleteMarker]);
  };





  const handlePolygonComplete = (polygon: google.maps.Polygon) => {
    const path: MapLocation[] = polygon.getPath().getArray().map((point) => ({
      lat: point.lat(),
      lng: point.lng(),
    }));

    setPolygonPaths(prev => [...prev, path]);
    setPolygonRefs(prev => [...prev, polygon]);
    setNewlyCreatedPolygonPath(prev => [...prev, path]);

    const index = newlyCreatedPolygonPath.length;

    const { lat, lng } = calculateCentroid(path);
    setLatitude(lat.toString());
    setLongitude(lng.toString());

    polygon.setOptions({
      editable: true,
      draggable: false,
      fillColor: "#ff0033",
      fillOpacity: 0.3,
      strokeColor: "#ff0033",
      strokeOpacity: 0.8,
      strokeWeight: 2,
    });

    addDeleteMarker(polygon);

    const updatePathOnEdit = () => {
      const updatedPath = polygon.getPath().getArray().map((point) => ({
        lat: point.lat(),
        lng: point.lng(),
      }));

      setNewlyCreatedPolygonPath(prev => {
        const newPaths = [...prev];
        newPaths[index] = updatedPath;
        return newPaths;
      });
    };

    polygon.getPath().addListener("set_at", updatePathOnEdit);
    polygon.getPath().addListener("insert_at", updatePathOnEdit);
    polygon.getPath().addListener("remove_at", updatePathOnEdit);
  };

  useEffect(() => {
    console.log("Current pinCode:", pinCode);
  }, [pinCode]);

  useEffect(() => {
    isDrawingModeRef.current = isDrawingMode;
  }, [isDrawingMode]);

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setOptions({ draggableCursor: isDrawingMode ? 'crosshair' : '' });
    polygonRefs.forEach(p => p.setOptions({ clickable: !isDrawingMode }));
    polygonDeleteMarkers.forEach(m => m.setClickable(!isDrawingMode));
  }, [isDrawingMode, polygonRefs, polygonDeleteMarkers]);

  const finishDrawing = () => {
    if (drawingVertices.length < 3) return;
    const polygon = new window.google.maps.Polygon({
      paths: drawingVertices.map(v => new window.google.maps.LatLng(v.lat, v.lng)),
      map: mapRef.current!,
    });
    handlePolygonComplete(polygon);
    setDrawingVertices([]);
    setIsDrawingMode(false);
  };

  const savePolygonGIS = async () => {
    if (!selectedAreaData) {
      alert("Area is missing.");
      return;
    }

    const groupid = `${Math.floor(Date.now() / 1000)}_${Math.floor(Math.random() * 1000)}`;
    const editedPolygons = editablePolygonPath.map((path, index) => {
      const original = originalEditablePolygonPaths[index];
      return JSON.stringify(path) !== JSON.stringify(original) ? path : original;
    });
    const updatedPolygonPathsNew = [
      ...editedPolygons,
      ...newlyCreatedPolygonPath,
    ].filter(path => path.length > 0);

    const updatedPolygonPaths = updatedPolygonPathsNew ? updatedPolygonPathsNew : [];
    const polygonData = {
      areaid: selectedAreaData.areaId,
      societyid: selectedCity,
      isActive: true,
      latlongarray: updatedPolygonPaths.length === 0 ? null : JSON.stringify(updatedPolygonPaths),
      groupid,
      latitude,
      longitude,
      path: savedPolygon?.path ?? "",
      pincode: pinCode,
      type: "polygon",
      networkPartner: networkPartner,
    };

    try {
      let response: AxiosResponse | null = null;

      if (!preLatLong) {
        const updateId = polygonId;
        if (updateId === undefined) {
          setErrorSnackbar(true);
          return;
        }
        response = await (dispatch(updatePolygon(updateId, polygonData)) as unknown as Promise<AxiosResponse>);
      } else {
        response = await (dispatch(createPolygon(polygonData)) as unknown as Promise<AxiosResponse>);
      }

      if (response?.data?.status === "success") {
        setSuccessSnackbar(true);
        setTimeout(() => {
          navigate('/master/manageViewPolygon');
        }, 700);
      } else {
        setErrorSnackbar(true);
        console.error("Unexpected response:", response);
      }
    } catch (error) {
      setErrorSnackbar(true);
      console.error("Error saving polygon:", error);
    }
  };



  const containerStyle = { width: "100%", height: "100vh" };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100vh" }}>
      <Box
        position="absolute"
        top={60}
        left={12}
        sx={{
          zIndex: 1, bgcolor: 'rgba(255, 255, 255, 0.9)', p: 2, borderRadius: 1,
          boxShadow: 3, width: { xs: '90%', sm: '400px' }, maxWidth: '600px',
        }} >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/master/manageViewPolygon")}
            sx={{ textTransform: "none", color: "black", padding: 0, minWidth: "auto" }}
          />
          <Typography variant="h4" sx={{ mb: 0, ml: 1 }}>
            Find Your Locality
          </Typography>
        </Box>

        <FormControl fullWidth sx={{ height: "36px" }}>
          <TextField
            label="Select City"
            value={selectedCity}
            select
            disabled
            sx={{ "& .MuiInputBase-input": { height: "36px", padding: "8px" } }}
            SelectProps={{
              MenuProps: { PaperProps: { sx: { maxHeight: "200px" } } },
            }}
          >
            {cities.map((city) => (
              <MenuItem key={city.id} value={city.name}>
                {city.name}
              </MenuItem>
            ))}
          </TextField>
        </FormControl>

        {selectedCity && (
          <>
            <Box display="flex" alignItems="center" gap={2} mt={4}>
              <FormControl fullWidth sx={{ height: "36px" }}>
                <TextField
                  label="Select Area"
                  value={selectedArea}
                  select
                  disabled
                  sx={{ "& .MuiInputBase-input": { height: "36px", padding: "8px" } }}
                  SelectProps={{
                    MenuProps: { PaperProps: { sx: { maxHeight: "200px" } } },
                  }}
                >
                  {areas.map((area) => (
                    <MenuItem key={area.areaId} value={area.area}>
                      {area.area}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
            </Box>

            {selectedArea && (
              <Box display="flex" alignItems="center" gap={2} mt={2}>
                <FormControl fullWidth sx={{ height: "36px", mt: 2 }}>
                  <TextField
                    label="Network Partner"
                    value={networkPartner}
                    onChange={(e) => setNetworkPartner(e.target.value)}
                    select
                    fullWidth
                    size="small"
                    sx={{ "& .MuiInputBase-input": { height: "26px", padding: "8px" } }}
                    SelectProps={{
                      MenuProps: { PaperProps: { sx: { maxHeight: "200px" } } },
                    }}
                  >
                    {networkPartners.length > 0 ? (
                      networkPartners.map((np) => (
                        <MenuItem key={np.id} value={np.partner_code}>
                          {np.partner_name}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem disabled>No Network Partners Available</MenuItem>
                    )}
                  </TextField>
                </FormControl>


              </Box>
            )}

            <Box display="flex" gap={1} sx={{ flexWrap: 'wrap', mt: 2 }}>
              <Button
                variant="contained"
                color={isDrawingMode ? "warning" : "secondary"}
                size="small"
                onClick={() => {
                  if (isDrawingMode) setDrawingVertices([]);
                  setIsDrawingMode(prev => !prev);
                }}
              >
                {isDrawingMode ? "Cancel Drawing" : "Draw Polygon"}
              </Button>
              {isDrawingMode && drawingVertices.length >= 3 && (
                <Button variant="contained" color="success" size="small" onClick={finishDrawing}>
                  Finish ({drawingVertices.length} pts)
                </Button>
              )}
            </Box>

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <Button variant="contained" color="info" onClick={savePolygonGIS}>
                Upload Polygon
              </Button>
            </Box>
          </>
        )}

      </Box>

      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={location || defaultCenter}
          zoom={15}
          onLoad={map => {
            mapRef.current = map;
            window.google.maps.event.addListener(map, 'click', (e: google.maps.MapMouseEvent) => {
              if (!isDrawingModeRef.current || !e.latLng) return;
              setDrawingVertices(prev => [...prev, { lat: e.latLng!.lat(), lng: e.latLng!.lng() }]);
            });
          }}
        >
          {location && (
            <Marker
              position={location}
              icon={{
                url: '/assets/images/Location-pin_Comway.svg',
                scaledSize: new window.google.maps.Size(40, 40),
              }}
            />
          )}
          {drawingVertices.length > 1 && (
            <Polygon
              paths={drawingVertices}
              options={{
                fillColor: "#ff0033",
                fillOpacity: 0.15,
                strokeColor: "#ff0033",
                strokeOpacity: 0.8,
                strokeWeight: 2,
                editable: false,
                draggable: false,
                clickable: false,
              }}
            />
          )}
        </GoogleMap>

      ) : (
        <Typography>Loading map...</Typography>
      )}
      <Snackbar
        open={errorSnackbar}
        autoHideDuration={6000}
        onClose={() => setErrorSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={() => setErrorSnackbar(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          Something went wrong. Please try again!
        </Alert>
      </Snackbar>

      <Snackbar
        open={successSnackbar}
        autoHideDuration={3000}
        onClose={() => setSuccessSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 9999 }}
      >
        <Alert
          onClose={() => setSuccessSnackbar(false)}
          severity="success"
          sx={{
            width: '100%',
            bgcolor: '#4caf50',
            color: '#ffffff',
            fontWeight: 'bold',
          }}
        >
          Polygon Updated Successfully
        </Alert>
      </Snackbar>
      <DeleteMessage
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirmDelete={handleConfirmDelete}
      />

    </Box>
  );
};

export default PolygonUpdateComponent;
